<template>
  <v-dialog v-model="showDialog" max-width="700" persistent @keydown.esc="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon start color="primary">mdi-package-variant</v-icon>
        Comanda Bunuri
      </v-card-title>

      <v-card-text>
        <v-form ref="orderForm" v-model="formValid">
          <!-- Good Category -->
          <v-select
            v-model="form.good_category_id"
            :items="goodCategories"
            item-value="id"
            item-title="name"
            label="Categorie *"
            :disabled="!!initialCategory"
            :rules="[v => !!v || 'Obligatoriu']"
            @update:model-value="onCategoryChange"
            variant="outlined"
            density="compact"
            class="mb-2"
          />

          <!-- Product Code -->
          <v-select
            v-model="form.product_code_id"
            :items="filteredProductCodes"
            item-value="id"
            item-title="name"
            label="Tip Produs *"
            :rules="[v => !!v || 'Obligatoriu']"
            variant="outlined"
            density="compact"
            class="mb-2"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <template #subtitle>
                  {{ item.raw.price }} {{ item.raw.currency }} / buc
                </template>
              </v-list-item>
            </template>
          </v-select>

          <!-- Network (for SIMs) -->
          <v-select
            v-if="selectedCategoryName === 'imsi'"
            v-model="form.network_id"
            :items="networks"
            item-value="id"
            item-title="name"
            label="Retea *"
            :rules="[v => !!v || 'Obligatoriu']"
            variant="outlined"
            density="compact"
            class="mb-2"
          />

          <!-- Quantity -->
          <v-text-field
            v-model.number="form.quantity"
            type="number"
            label="Cantitate *"
            min="1"
            max="1000"
            :rules="[v => v > 0 || 'Minim 1', v => v <= 1000 || 'Maxim 1000']"
            variant="outlined"
            density="compact"
            class="mb-2"
          />

          <!-- Client Selection (for distributors with subclients) -->
          <ClientHierarchySelect
            v-if="hasSubclients"
            v-model="form.client_id"
            :clients-list="clientsList"
            label="Comanda pentru client"
            placeholder="Selecteaza client..."
            auto-select-own
            class="mb-2"
            @change="onClientChange"
          />

          <v-divider class="my-4" />

          <!-- DELIVERY SECTION -->
          <DeliveryAddressSelector
            :show-toggle="false"
            :delivery-types="deliveryTypes"
            :client-id="form.client_id"
            @delivery-changed="onDeliveryChanged"
          />

          <v-divider class="my-4" />

          <!-- PRICE SUMMARY -->
          <OrderCostBreakdown
            :cost-lines="costBreakdownLines"
            :currency="currency"
            :total-amount="totalPrice"
          >
            <template #payment-info>
              <!-- Payment type message -->
              <v-alert
                v-if="isPrepaid"
                type="info"
                variant="tonal"
                density="compact"
                class="mt-3"
              >
                <v-icon start size="small">mdi-credit-card</v-icon>
                Plata se va face online pentru procesarea comenzii
              </v-alert>
              <v-alert
                v-else
                type="success"
                variant="tonal"
                density="compact"
                class="mt-3"
              >
                <v-icon start size="small">mdi-file-document</v-icon>
                Costul va fi adaugat pe factura lunara
              </v-alert>
            </template>
          </OrderCostBreakdown>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Anuleaza</v-btn>
        <v-btn
          color="primary"
          @click="submitOrder"
          :loading="loading"
          :disabled="!formValid"
        >
          <v-icon start>mdi-check</v-icon>
          {{ isPrepaid ? 'Continua la Plata' : 'Plaseaza Comanda' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { log } from "@/composables/useLog"
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import { getGoodsOrderOptions, createGoodsOrder } from "@/composables/useGoodsOrdersAPI"
import { getClientTree } from "@/composables/useGetClientsAPI"
import store from "@/store"
import ClientHierarchySelect from "@/components/order/ClientHierarchySelect.vue"
import OrderCostBreakdown from "@/components/order/OrderCostBreakdown.vue"
import DeliveryAddressSelector from "@/components/DeliveryAddressSelector.vue"

export default {
  name: 'GoodOrderDialog',
  components: {
    ClientHierarchySelect,
    OrderCostBreakdown,
    DeliveryAddressSelector
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    initialCategory: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue', 'orderCreated'],
  data() {
    return {
      showDialog: false,
      loading: false,
      formValid: false,
      token: null,
      user: null,

      // Options from API
      goodCategories: [],
      productCodes: [],
      networks: [],
      deliveryTypes: [],
      isPrepaid: true,
      currency: 'RON',

      // Client selection
      clientsList: [],

      // Form data
      form: {
        good_category_id: null,
        product_code_id: null,
        network_id: null,
        quantity: 1,
        client_id: null
      },

      // Delivery data from DeliveryAddressSelector
      deliveryData: {
        deliveryTypeId: null,
        deliveryTypeName: null,
        deliveryCost: 0,
        deliveryDetails: null,
        isValid: false
      }
    }
  },
  computed: {
    selectedCategoryName() {
      const cat = this.goodCategories.find(c => c.id === this.form.good_category_id)
      return cat?.name?.toLowerCase() || ''
    },
    filteredProductCodes() {
      if (!this.form.good_category_id) return this.productCodes
      return this.productCodes.filter(pc => pc.good_category_id === this.form.good_category_id)
    },
    unitPrice() {
      const pc = this.productCodes.find(p => p.id === this.form.product_code_id)
      return pc?.price || 0
    },
    goodsTotal() {
      return (this.form.quantity || 0) * this.unitPrice
    },
    deliveryCost() {
      return this.deliveryData.deliveryCost || 0
    },
    totalPrice() {
      return this.goodsTotal + this.deliveryCost
    },
    hasSubclients() {
      return this.clientsList && this.clientsList.length > 1
    },
    costBreakdownLines() {
      const lines = []
      // Goods line
      lines.push({
        label: `Bunuri (${this.form.quantity || 0} x ${this.unitPrice})`,
        amount: this.goodsTotal
      })
      // Delivery line
      lines.push({
        label: 'Livrare',
        amount: this.deliveryCost
      })
      return lines
    }
  },
  watch: {
    modelValue(val) {
      this.showDialog = val
      if (val) {
        this.loadOptions()
      }
    },
    showDialog(val) {
      this.$emit('update:modelValue', val)
    }
  },
  methods: {
    async loadOptions() {
      const { getLoggedIn, token } = useGetLoggedIn()
      await getLoggedIn()
      this.token = token.value
      this.user = store.getters.getUser

      // Load goods order options
      const response = await getGoodsOrderOptions(this.token)
      if (response.error) {
        log("Error loading options:", response.message)
        return
      }

      this.goodCategories = response.good_categories || []
      this.productCodes = response.product_codes || []
      this.networks = response.networks || []
      this.deliveryTypes = response.delivery_types || []
      this.isPrepaid = response.is_prepaid !== false
      this.currency = response.currency || 'RON'

      // Set initial category if provided
      if (this.initialCategory) {
        const cat = this.goodCategories.find(c => c.name?.toLowerCase() === this.initialCategory.toLowerCase())
        if (cat) {
          this.form.good_category_id = cat.id
        }
      }

      // Load clients hierarchy
      await this.loadClients()
    },

    async loadClients() {
      if (!this.user || !this.user.client_id) {
        log("No user or client_id found")
        return
      }
      try {
        const response = await getClientTree(this.token, this.user.client_id)
        if (!response.error && response.clients_list) {
          this.clientsList = this.buildClientsList(response.clients_list)
          // Pre-select user's own client
          if (this.clientsList.length > 0 && !this.form.client_id) {
            this.form.client_id = this.user.client_id
          }
        }
      } catch (error) {
        log("Error loading clients:", error)
      }
    },

    buildClientsList(clientsTree) {
      const list = []
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return
        clients.forEach(client => {
          const indent = level > 0 ? '—'.repeat(level) + ' ' : ''
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level
          })
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1)
          }
        })
      }
      addClientsRecursively(clientsTree)
      return list
    },

    onCategoryChange() {
      // Reset product code when category changes
      this.form.product_code_id = null
      this.form.network_id = null
    },

    onClientChange(data) {
      log("Client changed:", data)
      this.form.client_id = data.clientId
    },

    onDeliveryChanged(data) {
      log("Delivery changed:", data)
      this.deliveryData = {
        deliveryTypeId: data.deliveryTypeId,
        deliveryTypeName: data.deliveryTypeName,
        deliveryCost: data.deliveryCost || 0,
        deliveryDetails: data.deliveryDetails,
        isValid: data.isValid
      }
    },

    async submitOrder() {
      if (!this.$refs.orderForm.validate()) return

      this.loading = true
      try {
        const orderData = {
          ...this.form,
          delivery_type_id: this.deliveryData.deliveryTypeId,
          delivery_cost: this.deliveryData.deliveryCost,
          delivery_details: this.deliveryData.deliveryDetails
        }

        const response = await createGoodsOrder(this.token, orderData)

        if (response.error) {
          alert(response.message)
        } else {
          this.$emit('orderCreated', response)
          if (response.payment_url) {
            // Redirect to payment
            window.location.href = response.payment_url
          } else {
            alert(response.message || 'Comanda a fost plasata cu succes!')
            this.close()
          }
        }
      } catch (err) {
        log("Error submitting order:", err)
        alert("Eroare la plasarea comenzii")
      } finally {
        this.loading = false
      }
    },

    close() {
      this.showDialog = false
      this.resetForm()
    },

    resetForm() {
      this.form = {
        good_category_id: null,
        product_code_id: null,
        network_id: null,
        quantity: 1,
        client_id: null
      }
      this.deliveryData = {
        deliveryTypeId: null,
        deliveryTypeName: null,
        deliveryCost: 0,
        deliveryDetails: null,
        isValid: false
      }
    }
  }
}
</script>
